// Pure helpers for the V3 profile and Access Pass views (no network, no storage).
// Catalog and payloads follow the contract proposals C.2 / C.7; real Core 1.1 data is read where it exists.
import { own, text, validIsoDate } from "./memberDerive";
import { CORE_PASS_LABELS } from "./coreShapes";

const isRec = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const str = (v) => (typeof v === "string" ? v.trim() : "");
const len = (v) => Array.from(String(v ?? "")).length;
const iso = (v) => (validIsoDate(v) ? v : null);
const idOf = (v) => (typeof v === "number" && Number.isFinite(v) ? String(v) : typeof v === "string" && v.trim() && !/[\u0000-\u001f\u007f/\\]/.test(v) ? v : "");

export const NOTE_MAX = 300;
export const PURPOSE_MAX = 120;

const LEVEL = [["required", "Erforderlich"], ["helpful", "Hilfreich"]];

// M84–M89: route key, Core key (C.7), fields [key, label, options] and the free note; "" = Keine Angabe.
export const PASS_SECTIONS = [
  { key: "mobilitaet", core: "mobility", m: "M84", title: "Mobilität und Zugang", intro: "Diese Angaben bleiben privat, bis du sie gezielt freigibst.",
    fields: [["step_free", "Stufenloser Zugang", LEVEL], ["seating", "Sitzmöglichkeit", LEVEL], ["level_path", "Ebener Weg", LEVEL]], note: ["Eigener Hinweis", "Freiwillig ergänzen"] },
  { key: "kommunikation", core: "communication", m: "M85", title: "Kommunikation", intro: "Wähle, wie du bei gemeinsamen Treffen gerne kommunizierst.",
    fields: [["mode", "Kommunikation", [["written", "Schriftlich bevorzugt"], ["spoken", "Mündlich bevorzugt"], ["both", "Beides möglich"]]],
      ["messages", "Nachrichten", [["short", "Kurz und verständlich"], ["plain", "In einfacher Sprache"], ["detailed", "Ausführlich"]]],
      ["response_time", "Antwortzeit", [["more_time", "Etwas mehr Zeit ist hilfreich"], ["usual", "Übliche Antwortzeit"]]],
      ["calls", "Anrufe", [["arrange", "Bitte vorher absprechen"], ["ok", "Anrufe sind möglich"], ["none", "Bitte keine Anrufe"]]]] },
  { key: "ruhe", core: "sensory", m: "M86", title: "Ruhe und Reize", intro: "Beschreibe freiwillig, welche Umgebung für dich angenehm ist.",
    fields: [["quiet_area", "Ruhiger Bereich", LEVEL], ["low_flash", "Wenig Blitzlicht", LEVEL], ["clear_space", "Übersichtliche Umgebung", LEVEL]], note: ["Eigener Hinweis", "Freiwillig ergänzen"] },
  { key: "pausen", core: "pace", m: "M87", title: "Pausen und Tempo", intro: "Du bestimmst, welches Tempo und welche Pausen zu dir passen.",
    fields: [["breaks", "Pausen", [["as_needed", "Nach Bedarf"], ["regular", "Regelmässig"]]], ["short_paths", "Kurze Wege", LEVEL],
      ["pace", "Tempo", [["flexible", "Flexibel vereinbaren"], ["calm", "Ruhiges Tempo"]]]], note: ["Eigener Hinweis", "Freiwillig ergänzen"] },
  { key: "begleitung", core: "assistance", m: "M88", title: "Begleitung und Assistenz", intro: "Besprich Begleitung und Assistenz bezogen auf das konkrete Treffen.",
    fields: [["companion", "Begleitung", [["wanted", "Bei diesem Treffen gewünscht"], ["own", "Ich komme mit Begleitperson"], ["not_needed", "Nicht nötig"]]],
      ["assistance_dog", "Assistenzhund", [["yes", "Ich komme mit Assistenzhund"], ["no", "Kein Assistenzhund"]]],
      ["arrangement", "Absprache", [["before", "Vor dem Treffen"], ["on_site", "Vor Ort"]]]], note: ["Weitere Person", "Namen nur mit deren Zustimmung teilen"] },
  { key: "anreise", core: "travel", m: "M89", title: "Anreise", intro: "Halte hilfreiche Anreisehinweise ohne genaue Wohnadresse fest.",
    fields: [["directions", "Wegbeschreibung", [["simple_written", "Einfach und schriftlich"], ["with_pictures", "Mit Bildern"], ["spoken", "Mündlich erklärt"]]],
      ["meeting_point", "Treffpunkt", [["public", "Öffentlicher Ort"], ["entrance", "Direkt am Eingang"]]], ["transfer_help", "Umstiegshilfe", LEVEL]], note: ["Planzeiten", "Eigene freiwillige Planung"] },
];

// M19 shows four cards; two of them bundle two sections.
export const PASS_GROUPS = [
  { key: "mobilitaet", title: "Mobilität und Zugang", sections: ["mobilitaet"] },
  { key: "kommunikation", title: "Kommunikation", sections: ["kommunikation"] },
  { key: "ruhe-tempo", title: "Ruhe, Pausen und Tempo", sections: ["ruhe", "pausen"] },
  { key: "begleitung-anreise", title: "Begleitung und Anreise", sections: ["begleitung", "anreise"] },
];

export const passSection = (key) => PASS_SECTIONS.find((s) => s.key === key) || null;
const byCore = (core) => PASS_SECTIONS.find((s) => s.core === core) || null;
const optionLabel = (options, v) => (options.find(([o]) => o === v) || [])[1] || "";

// GET /member/access-pass (C.7) → { [routeKey]: { values, note, updatedAt } }; null when no sections object exists.
export function normalizePass(d) {
  const sections = own(d, "sections");
  if (!isRec(sections)) return null;
  const out = {};
  for (const s of PASS_SECTIONS) {
    const raw = own(sections, s.core);
    const values = {};
    for (const [k, , options] of s.fields) {
      const v = own(own(raw, "values"), k);
      if (typeof v === "string" && optionLabel(options, v)) values[k] = v;
    }
    const note = s.note ? str(own(raw, "note")) : "";
    out[s.key] = { values, note: len(note) <= NOTE_MAX ? note : "", updatedAt: iso(own(raw, "updated_at")) };
  }
  return out;
}

export const emptySection = () => ({ values: {}, note: "", updatedAt: null });

// Saved entries of one section as [label, value] pairs (note last).
export function sectionEntries(section, data) {
  if (!section || !data) return [];
  const rows = section.fields.filter(([k]) => data.values[k]).map(([k, label, options]) => [label, optionLabel(options, data.values[k])]);
  return data.note && section.note ? [...rows, [section.note[0], data.note]] : rows;
}

export function sectionSummary(section, data) {
  const first = sectionEntries(section, data)[0];
  return first ? first[1] : "Keine Angabe";
}

// PUT body for one section; only known options, note within limits.
// Sections without a free note (M85 Kommunikation) never send one.
export function sectionPayload(section, form) {
  const note = section.note ? str(form.note) : "";
  if (len(note) > NOTE_MAX) return { error: `Der Hinweis darf höchstens ${NOTE_MAX} Zeichen lang sein.`, body: null };
  const values = Object.fromEntries(section.fields.filter(([k, , options]) => optionLabel(options, form.values[k])).map(([k]) => [k, form.values[k]]));
  return { error: "", body: { values, note } };
}

// Label for a share/grant field key: Core field_labels, then the V3 catalog (mobility.step_free), then the Core pass fields.
export function fieldLabel(key, labels) {
  const own_ = own(labels, key);
  if (typeof own_ === "string" && own_.trim()) return own_.trim();
  const [core, field] = String(key).split(".");
  const s = byCore(core);
  if (s && field === "note" && s.note) return s.note[0];
  const f = s && s.fields.find(([k]) => k === field);
  if (f) return f[1];
  return own(CORE_PASS_LABELS, key) || "Unbekanntes Feld";
}

// Shareable entries: only saved values, never empty fields.
export function shareOptions(pass) {
  if (!pass) return [];
  return PASS_SECTIONS.flatMap((s) => {
    const d = pass[s.key];
    if (!d) return [];
    const rows = s.fields.filter(([k]) => d.values[k]).map(([k, label, options]) => ({ id: `${s.core}.${k}`, label, value: optionLabel(options, d.values[k]), section: s.title }));
    return d.note && s.note ? [...rows, { id: `${s.core}.note`, label: s.note[0], value: d.note, section: s.title }] : rows;
  });
}

const GRANT_STATE = { aktiv: "active", active: "active", abgelaufen: "expired", expired: "expired", widerrufen: "revoked", revoked: "revoked" };

// grants[] of GET /member/access-pass/requests (Core 1.1); a passed expiry or revoked_at always ends a grant.
export function normalizePassGrants(d, now = Date.now()) {
  if (!isRec(d)) return null;
  const list = own(d, "grants");
  if (!Array.isArray(list)) return Array.isArray(own(d, "requests")) ? [] : null;
  return list.filter(isRec).map((g) => {
    const raw = own(g, "state") ?? own(g, "status");
    let state = typeof raw === "string" && Object.prototype.hasOwnProperty.call(GRANT_STATE, raw.toLowerCase()) ? GRANT_STATE[raw.toLowerCase()] : "unknown";
    const expiresAt = iso(own(g, "expires_at"));
    const revokedAt = iso(own(g, "revoked_at"));
    if (revokedAt) state = "revoked";
    else if (state !== "revoked" && expiresAt && new Date(expiresAt).getTime() <= now) state = "expired";
    const fields = Array.isArray(own(g, "fields")) ? g.fields.filter((f) => typeof f === "string") : [];
    return {
      id: idOf(own(g, "id")),
      recipient: str(own(g, "recipient_name")) || "Empfänger nicht angegeben",
      kind: idOf(own(g, "recipient_member_id")) ? "member" : "team",
      purpose: str(text(own(g, "purpose"))) || "Kein Zweck angegeben",
      fields: fields.map((f) => fieldLabel(f, own(g, "field_labels"))),
      expiresAt, revokedAt, state, ended: state === "expired" || state === "revoked",
      endedAt: revokedAt || (state === "expired" ? expiresAt : null),
    };
  }).filter((g) => g.id);
}

export const GRANT_STATE_LABEL = { active: "Aktiv", expired: "Abgelaufen", revoked: "Widerrufen", unknown: "Status unbekannt" };

const TZ = "Europe/Zurich";
function parts(v, opts) {
  const d = iso(v) ? new Date(v) : null;
  if (!d) return null;
  return Object.fromEntries(new Intl.DateTimeFormat("de-CH", { timeZone: TZ, ...opts }).formatToParts(d).map((x) => [x.type, x.value]));
}

export function dayOnly(v) {
  const p = parts(v, { day: "2-digit", month: "2-digit", year: "numeric" });
  return p ? `${p.day}.${p.month}.${p.year}` : "Nicht angegeben";
}

export function dayTime(v, uhr = true) {
  const p = parts(v, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  return p ? `${p.day}.${p.month}.${p.year} · ${p.hour}:${p.minute}${uhr ? " Uhr" : ""}` : "Nicht angegeben";
}

// "Kaffeetreffen · Stufenloser Zugang · Pausen · bis 31.10.2026" (M110 context column).
export function grantContext(g) {
  const head = [g.purpose, g.fields.join(" · ") || "Keine Felder angegeben"];
  if (g.state === "revoked") return [...head, g.revokedAt ? `widerrufen am ${dayOnly(g.revokedAt)}` : "widerrufen"].join(" · ");
  if (g.state === "expired") return [...head, "abgelaufen"].join(" · ");
  return [...head, g.expiresAt ? `bis ${dayOnly(g.expiresAt)}` : "ohne Enddatum"].join(" · ");
}

// Server preview of POST /member/access-pass/shares/preview (C.7); null without hash or with an unexpected form.
export function normalizeSharePreview(d) {
  const p = own(d, "preview");
  const hash = own(p, "preview_hash");
  const fields = own(p, "fields");
  if (!isRec(p) || typeof hash !== "string" || !hash || !Array.isArray(fields) || !fields.every((f) => typeof f === "string")) return null;
  return {
    recipientId: idOf(own(own(p, "recipient"), "id")), recipient: str(own(own(p, "recipient"), "name")) || "Nicht angegeben",
    purpose: str(own(p, "purpose")), fieldKeys: fields, fields: fields.map((f) => fieldLabel(f)), expiresAt: iso(own(p, "expires_at")), hash,
  };
}

// The confirmation must show exactly what was chosen; any difference blocks sending.
export function previewMatches(preview, form) {
  if (!preview) return false;
  const a = [...preview.fieldKeys].sort().join("|");
  const b = [...form.fields].sort().join("|");
  return a === b && preview.recipientId === String(form.recipientId) && preview.purpose === str(form.purpose);
}

export const createdGrantId = (d) => idOf(own(own(d, "grant"), "id"));

// Recipient side (C.7): list and detail; values only from the catalog, unknown keys are dropped.
export function normalizeReceivedList(d) {
  const list = own(d, "shares");
  if (!Array.isArray(list)) return null;
  return list.filter(isRec).map((s) => ({
    id: idOf(own(s, "id")), owner: str(own(own(s, "owner"), "first_name")) || str(own(s, "owner_first_name")) || "Nicht angegeben",
    purpose: str(own(s, "purpose")) || "Kein Zweck angegeben", expiresAt: iso(own(s, "expires_at")),
  })).filter((s) => s.id);
}

export function normalizeReceived(d) {
  const s = own(d, "share");
  if (!isRec(s) || !idOf(own(s, "id")) || !Array.isArray(own(s, "fields"))) return null;
  const fields = s.fields.filter(isRec).map((f) => {
    const key = own(f, "key");
    const [core, field] = typeof key === "string" ? key.split(".") : [];
    const sec = byCore(core);
    if (!sec) return null;
    if (field === "note") { const v = sec.note ? str(own(f, "value")) : ""; return v && len(v) <= NOTE_MAX ? { key, label: sec.note[0], value: v } : null; }
    const def = sec.fields.find(([k]) => k === field);
    const value = def && typeof own(f, "value") === "string" ? optionLabel(def[2], f.value) : "";
    return value ? { key, label: def[1], value } : null;
  }).filter(Boolean);
  return { id: idOf(s.id), owner: str(own(s, "owner_first_name")) || "Nicht angegeben", purpose: str(own(s, "purpose")) || "Kein Zweck angegeben", expiresAt: iso(own(s, "expires_at")), fields };
}

export const isPast = (v, now = Date.now()) => Boolean(iso(v)) && new Date(v).getTime() <= now;

// Own membership card from GET /member/profile (Core 1.1 access_pass); shown only to its owner.
export function ownPass(profileResponse) {
  const p = own(profileResponse, "access_pass");
  if (own(p, "has_pass") !== true) return null;
  return { number: str(own(p, "pass_number")), validUntil: iso(own(p, "valid_until")) };
}

// Short-lived presentation code only from the Core; never the pass number, nothing generated locally.
export function presentationCode(profileResponse, now = Date.now()) {
  const pass = own(profileResponse, "access_pass");
  const p = own(pass, "presentation");
  const code = own(p, "code");
  const exp = iso(own(p, "expires_at"));
  if (typeof code !== "string" || !/^[A-Z0-9-]{6,24}$/.test(code) || !exp || new Date(exp).getTime() <= now) return null;
  if (code === own(pass, "pass_number")) return null;
  const rows = own(own(p, "qr"), "rows");
  const n = Array.isArray(rows) ? rows.length : 0;
  const valid = n >= 21 && n <= 177 && (n - 17) % 4 === 0 && rows.every((r) => typeof r === "string" && r.length === n && /^[01]+$/.test(r));
  return { code, expiresAt: exp, rows: valid ? rows : null };
}

// Profile (M18, M82): visibility per field (C.2) and which fields the Core lets the member change.
export const VISIBILITY = [["members", "Nur Mitglieder"], ["contacts", "Bestätigte Kontakte"], ["only_me", "Nur ich"]];
export const VISIBILITY_FIELDS = [["name", "Anzeigename"], ["city", "Stadt"], ["interests", "Interessen"], ["bio", "Kurzbeschreibung"]];
const isVisibility = (v) => VISIBILITY.some(([k]) => k === v);
export const visibilityLabel = (v) => (VISIBILITY.find(([k]) => k === v) || [, "Je Feld unterschiedlich"])[1];

export function readVisibility(profile) {
  const v = own(own(profile, "prefs"), "profile_visibility");
  const values = Object.fromEntries(VISIBILITY_FIELDS.map(([k]) => [k, isRec(v) && isVisibility(own(v, k)) ? v[k] : "members"]));
  return { supported: isRec(v), values };
}

export const overallVisibility = (values) => {
  const all = VISIBILITY_FIELDS.map(([k]) => values[k]);
  return all.every((x) => x === all[0]) ? all[0] : "mixed";
};

const CORE_EDITABLE = ["bio", "interests", "prefs"];
export function editableFields(profile) {
  const e = own(profile, "editable");
  return Array.isArray(e) ? e.filter((x) => typeof x === "string") : CORE_EDITABLE;
}

export const NAME_MAX = 60;
export const CITY_MAX = 80;
export const BIO_MAX = 300;

export function profileDraft(profile) {
  return { name: str(own(profile, "name")), city: str(own(profile, "city")), bio: str(text(own(profile, "bio"))), visibility: overallVisibility(readVisibility(profile).values) };
}

export function profileErrors(draft) {
  const e = {};
  if (!str(draft.name)) e.name = "Bitte gib einen Anzeigenamen ein.";
  else if (len(str(draft.name)) > NAME_MAX) e.name = `Der Anzeigename darf höchstens ${NAME_MAX} Zeichen lang sein.`;
  if (len(str(draft.city)) > CITY_MAX) e.city = `Die Stadt darf höchstens ${CITY_MAX} Zeichen lang sein.`;
  if (len(str(draft.bio)) > BIO_MAX) e.bio = `„Kurz über mich“ darf höchstens ${BIO_MAX} Zeichen lang sein.`;
  return e;
}

const FIELD_NAME = { name: "Anzeigename", city: "Stadt", bio: "Kurz über mich", visibility: "Profil-Sichtbarkeit" };
export const changeLabel = (k) => FIELD_NAME[k];

// Which changes exist, which the Core can store now, and the PATCH body for those only.
export function profileChanges(draft, profile) {
  const base = profileDraft(profile);
  const editable = editableFields(profile);
  const vis = readVisibility(profile);
  const can = { name: editable.includes("name"), city: editable.includes("city"), bio: editable.includes("bio"), visibility: vis.supported && editable.includes("prefs") };
  const changed = ["name", "city", "bio", "visibility"].filter((k) => str(draft[k]) !== base[k]);
  const blocked = changed.filter((k) => !can[k] || (k === "visibility" && !isVisibility(draft.visibility)));
  const body = {};
  for (const k of ["name", "city", "bio"]) if (changed.includes(k) && !blocked.includes(k)) body[k] = str(draft[k]);
  if (changed.includes("visibility") && !blocked.includes("visibility")) {
    body.prefs = { ...(own(profile, "prefs") || {}), profile_visibility: Object.fromEntries(VISIBILITY_FIELDS.map(([k]) => [k, draft.visibility])) };
  }
  return { changed, blocked, body };
}

// Per-field visibility used by the member preview (draft overall value wins unless it is "mixed").
export function previewVisibility(draft, profile) {
  const stored = readVisibility(profile).values;
  return isVisibility(draft.visibility) ? Object.fromEntries(VISIBILITY_FIELDS.map(([k]) => [k, draft.visibility])) : stored;
}

export const initials = (name) => str(name).split(/\s+/).filter(Boolean).map((p) => p[0]).join("").slice(0, 2).toUpperCase();
